import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useKanbanStyleUpdater from '../../hooks/UseKanbanStyleUpdater';
import {
  GetJobCandidateRatingCounts,
  GetJobCandidates,
  GetJobCandidatesByAdvertId,
} from '../../services/JobsService';
import { KanbanCardTagStyle } from '../../util/Definitions/Kanban';
import { JobTracking } from '../../common/models/Jobs/JobTracking';
import PersonIcon from '@mui/icons-material/Person';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import Avatar from '@mui/material/Avatar';
import { useNavigate } from 'react-router-dom';
import { Job, JobCandidateKanbanGroup } from '../../common/models/Jobs/Job';
import {
  GetActivitiesSettings,
  GetCachedRatingReasons,
  GetInterviewWorkflowStatuses,
  GetKanbanGroups,
  GetOfferAcceptedWorkflowStatus,
  GetOfferedWorkflowStatus,
  GetPlacementsSettings,
} from '../../services/ConfigurationService';
import { KanbanGroupingColumn } from '../../common/models/Jobs/JobTracking';
import { MenuOptionDefinition } from 'common/models/MenuDefinition';
import ContextMenu from 'components/Menus/ContextMenu';
import { PopperProps } from '@mui/material/Popper';
import { ActivitySettings } from 'common/models/Configuration/Activities';
import { GetMarketingPlatformsAvailable, GetMySettings } from 'services/UsersService';
import { companyHasSinglePermission, userHasSinglePermission } from 'util/PermissionsUtils';
import { Permission } from 'common/models/Permissions';
import { MeetingType } from 'common/models/Configuration/MeetingType';
import { GetActiveMeetingTypes } from 'services/MeetingsService';
import { GetActiveInterviewTypes } from 'services/InterviewsService';
import { InterviewType } from 'common/models/Configuration/InterviewType';
import { UnlinkCandidateData } from 'common/models/Candidates/Candidate';
import { EmailRecipient } from 'common/models/EmailRecipient';
import { RecordExportData } from 'common/models/Common';
import SendEmailDialog from 'components/Dialogs/Messages/SendEmailDialog';
import SendSmsDialog from 'components/Dialogs/Messages/SendSmsDialog';
import ExportRecordsDialog from 'components/Dialogs/ExportRecordsDialog';
import TagsManagementDialog from 'components/Dialogs/TagsManagementDialog';
import ConfirmationDialog from 'components/Dialogs/Generic/ConfirmationDialog';
import {
  ExportCandidateToDeputy,
  ExportCandidateToFoundU,
  UpdateCandidateJobRating_Bulk,
  VerifyRejectionEmailByCandidateList,
} from 'services/CandidatesService';
import LinkCandidatesToJobDialog from 'components/Dialogs/Candidates/LinkCandidatesToJobDialog';
import UnlinkCandidatesFromJobDialog from 'components/Dialogs/Candidates/UnlinkCandidatesFromJobDialog';
import EditActivityDialog from 'components/Dialogs/Activities/EditActivityDialog';
import { DefaultActivity } from 'util/Definitions/Activities';
import { Activity } from 'common/models/Activities';
import SendReferralDialog from 'components/Dialogs/SendReferralDialog';
import ExportRecordsToMailchimpDialog from 'components/Dialogs/ExportRecordsToMailchimpDialog';
import ExportRecordsToCampaignMonitorDialog from 'components/Dialogs/ExportRecordsToCampaignMonitorDialog';
import { RatingReason } from 'common/models/Configuration/Jobs';
import { GetIntegration } from 'services/CustomerIntegrationsService';
import useWorkflowOrder from 'hooks/UseWorkflowOrder';
import PlaceCandidatesDialog from 'components/Dialogs/Candidates/PlaceCandidatesDialog';
import DigitalReferenceCheckDialog from 'components/Dialogs/Candidates/DigitalReferenceCheckDialog';
import ReferooActionsDialog from 'components/Dialogs/Candidates/ReferooActionsDialog';
import OfferMadeDialog from 'components/Dialogs/Candidates/OfferMadeDialog';
import RejectionEmailDialog from 'components/Dialogs/Candidates/RejectionEmailDialog';

interface Props {
  jobId: number;
  job?: Job;
  advertId?: number;
  extraActions?: JSX.Element;
  loadingHandler?: (isLoading: boolean) => void;
  errorHandler?: (message: string) => void;
  successHandler?: (message: string) => void;
}

interface KanbanCard {
  id: number;
  column: number;
  title: string;
  candidateID: number;
  jobID: number;
  ratingBgColor: string;
  ratingTextColor: string;
  statusName: string;
  statusLastUpdatedValue: string;
  data: JobTracking;
  source: string;
}

interface KanbanApi {
  getCard: (rowId: number) => { data?: JobTracking } | undefined;
  destructor: () => void;
  // Add other methods as needed
}

const ratingStyle = {
  '3': {
    background: '#C0C0C0', //grey
    text: '#696969',
  },
  '1': {
    background: '#9bc2e6', //blue
    text: '#203764',
  },
  '2': {
    background: '#ffeb9c', //yellow
    text: '#9c6500',
  },
  '0': {
    background: '#ffc7ce', //red
    text: '#9c0006',
  },
  '4': {
    background: '#c6efce', //green
    text: '#006100',
  },
};

const kanbanElementDomId = 'candidates-kanban';
const greenBgColor = '#1da826';
const yellowBgColor = '#fecc02';
const blueBgColor = '#0366cc';
const redBgColor = '#cc0303';
const emptyRatingCounts = [0, 0, 0, 0, 0, 0];

const getKanbanCardDomId = (rowId: number) => {
  return `candidate-kanban-card-${rowId}`;
};

const addContextMenuListeners = (rowIds: number[], eventListener: (event: MouseEvent) => void) => {
  for (let i = 0; i < rowIds.length; i++) {
    const id = rowIds[i];
    const card = document.getElementById(getKanbanCardDomId(id));
    if (card) card.addEventListener('contextmenu', eventListener);
  }
};

const removeContextMenuListeners = (
  rowIds: number[],
  eventListener: (event: MouseEvent) => void
) => {
  for (let i = 0; i < rowIds.length; i++) {
    const id = rowIds[i];
    const card = document.getElementById(getKanbanCardDomId(id));
    if (card) card.removeEventListener('contextmenu', eventListener);
  }
};

const compareRatingReasons = (a: RatingReason, b: RatingReason) => {
  if (b.value < a.value) return 1;
  if (b.value > a.value) return -1;
  return 0;
};

const sortDefinitionByLabel = (a: MenuOptionDefinition, b: MenuOptionDefinition) =>
  a.label > b.label ? 1 : b.label > a.label ? -1 : 0;

const entityTypeId = 3;

export default function CandidateKanbanComponent({
  jobId,
  job,
  advertId = 0,
  extraActions,
  loadingHandler,
  errorHandler,
  successHandler,
}: Props) {
  const [contextMenuAnchor, setContextMenuAnchor] = useState<PopperProps['anchorEl']>(null);
  const [rawData, setRawData] = useState<JobTracking[] | null>(null);
  const [rowIds, setRowIds] = useState<number[]>([]);
  const [kanbanGroupings, setKanbanGroupings] = useState<JobCandidateKanbanGroup[]>([]);
  const [isSetupFinished, setIsSetupFinished] = useState(false);
  const [ratingCounts, setRatingCounts] = useState(emptyRatingCounts);
  const [ratingIdFilter, setRatingIdFilter] = useState<number>();
  const [isInitialQuerySetupFinished, setIsInitialQuerySetupFinished] = useState(false);
  const [styleUpdaterTrigger, setStyleUpdaterTrigger] = useState(false);
  const [activitySettings, setActivitySettings] = useState<ActivitySettings[]>([]);
  const [hasMailChimpAccess, setHasMailChimpAccess] = useState(false);
  const [hasCampaignMonitorAccess, setHasCampaignMonitorAccess] = useState(false);
  const [meetingTypes, setMeetingTypes] = useState<MeetingType[]>([]);
  const [selectedCardData, setSelectedCardData] = useState<JobTracking | null>(null);
  const [interviewTypes, setInterviewTypes] = useState<InterviewType[]>([]);
  const [showTagManagementDialog, setShowTagManagementDialog] = useState(false);
  const [showLinkCandidateToJobDialog, setShowLinkCandidateToJobDialog] = useState(false);
  const [unlinkCandidateData, setUnlinkCandidateData] = useState<UnlinkCandidateData[]>([]);
  const [showSendReferralDialog, setShowSendReferralDialog] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<ActivitySettings | null>(null);
  const [sendMessageDialogType, setSendMessageDialogType] = useState<
    'email' | 'sms' | 'marketing-list' | null
  >(null);
  const [exportRecordDialogType, setExportRecordDialogType] = useState<
    'mailchimp' | 'campaign-monitor' | null
  >(null);
  const [messageRecipients, setMessageRecipients] = useState<EmailRecipient[]>([]);
  const [exportRecords, setExportRecords] = useState<RecordExportData[]>([]);

  const [interviewWorkflowStatuses, setInterviewWorkflowStatuses] = useState<number[]>([]);
  const [offerWorkflowStatus, setOfferWorkflowStatus] = useState<number>(0);
  const [canBulkPlacements, setCanBulkPlacements] = useState(false);
  const [yellowReasons, setYellowReasons] = useState<RatingReason[]>([]);
  const [blueReasons, setBlueReasons] = useState<RatingReason[]>([]);
  const [redReasons, setRedReasons] = useState<RatingReason[]>([]);
  const [isDeputyConnected, setIsDeputyConnected] = useState(false);
  const [isFoundUConnected, setIsFoundUConnected] = useState(false);
  const [isReferooConnected, setIsReferooConnected] = useState(false);
  const [showExportToDeputyDialog, setShowExportToDeputyDialog] = useState(false);
  const [showExportToFoundUDialog, setShowExportToFoundUDialog] = useState(false);
  const [showReferooDialog, setShowReferooDialog] = useState(false);
  const [showPlaceCandidatesDialog, setShowPlaceCandidatesDialog] = useState(false);
  const [showDigitalReferenceCheckDialog, setShowDigitalReferenceCheckDialog] = useState(false);
  const [offerAcceptedWorkflowStatus, setOfferAcceptedWorkflowStatus] = useState<number>(0);
  const [showOfferMadeDialog, setShowOfferMadeDialog] = useState(false);
  const [rejectionEmailRecipients, setRejectionEmailRecipients] = useState<number[]>([]);
  const [bulkRejectionReason, setBulkRejectionReason] = useState('');
  const [defaultRejectionTemplateId, setDefaultRejectionTemplateId] = useState(0);
  const workflowOrder = useWorkflowOrder();

  const navigate = useNavigate();
  const kanbanRef = useRef<KanbanApi | null>(null);

  useKanbanStyleUpdater(isSetupFinished, undefined, styleUpdaterTrigger);

  const canExportCandidates = useMemo(
    () =>
      companyHasSinglePermission(Permission.CandidatesExport) &&
      userHasSinglePermission(Permission.CandidatesExport),
    []
  );

  const userCanAddEditCandidates = useMemo(
    () =>
      userHasSinglePermission(Permission.CandidatesAddEdit) &&
      companyHasSinglePermission(Permission.CandidatesAddEdit),
    []
  );

  const userCanEmailMarket = useMemo(
    () => userHasSinglePermission(Permission.CandidatesExport),
    []
  );

  const userCanSendEmail = useMemo(
    () =>
      userHasSinglePermission(Permission.MessageCentre) &&
      companyHasSinglePermission(Permission.MessageCentre),
    []
  );

  const userCanSendSms = useMemo(
    () =>
      userHasSinglePermission(Permission.MessageCentreSms) &&
      companyHasSinglePermission(Permission.MessageCentreSms),
    []
  );

  const getKanbanData = useCallback(async () => {
    loadingHandler?.(true);
    const [res, grouping] = await Promise.all([
      advertId === 0
        ? GetJobCandidates(jobId, errorHandler)
        : GetJobCandidatesByAdvertId(jobId, advertId, errorHandler),
      GetKanbanGroups(true),
    ]);

    if (res) {
      setRawData(res);
      const ids = res.map((r) => r.jobTrackingID);
      setRowIds(ids);
    }

    if (grouping) {
      setKanbanGroupings(grouping);
    }
    loadingHandler?.(false);
  }, [advertId, errorHandler, jobId, loadingHandler]);

  useEffect(() => {
    getKanbanData();
  }, [getKanbanData]);

  useEffect(() => {
    const getMeetingTypes = async () => {
      const res = await GetActiveMeetingTypes(entityTypeId);
      if (res) setMeetingTypes(res);
    };
    getMeetingTypes();
  }, []);

  useEffect(() => {
    const getInterviewTypes = async () => {
      const res = await GetActiveInterviewTypes();
      if (res) setInterviewTypes(res);
    };
    getInterviewTypes();
  }, []);

  useEffect(() => {
    const getInterviewStatuses = async () => {
      const interviewStutuses = await GetInterviewWorkflowStatuses();
      if (interviewStutuses) {
        setInterviewWorkflowStatuses(interviewStutuses);
      }
    };
    getInterviewStatuses();
  }, []);

  useEffect(() => {
    const getOfferedStatuses = async () => {
      const offeredStatsus = await GetOfferedWorkflowStatus();
      if (offeredStatsus) {
        setOfferWorkflowStatus(offeredStatsus);
      }
    };
    getOfferedStatuses();
  }, []);

  useEffect(() => {
    const getOfferedAcceptedStatuses = async () => {
      const offerAcceptedStatus = await GetOfferAcceptedWorkflowStatus();
      if (offerAcceptedStatus) {
        setOfferAcceptedWorkflowStatus(offerAcceptedStatus);
      }
    };
    getOfferedAcceptedStatuses();
  }, []);

  useEffect(() => {
    const getPlacementSettings = async () => {
      const placementSettings = await GetPlacementsSettings();
      if (placementSettings) {
        const p = placementSettings.find((s) => s.type === 'BulkPlacements');
        if (p && p.value.toLowerCase() === 'true') setCanBulkPlacements(true);
      }
    };
    getPlacementSettings();
  }, []);

  useEffect(() => {
    const getData = async () => {
      const foundU = await GetIntegration(16);
      if (foundU) setIsFoundUConnected(foundU.statusID === 1);

      const referoo = await GetIntegration(1);
      if (referoo) setIsReferooConnected(referoo.statusID === 1);

      const deputy = await GetIntegration(27);
      if (deputy) setIsDeputyConnected(deputy.statusID === 1);
    };
    getData();
  }, []);

  useEffect(() => {
    const getSettings = async () => {
      const mySettings = await GetMySettings();
      if (mySettings) {
        const templateSetting = mySettings.find(
          (s) => s.type === 'DefaultRejectionEmailTemplateID'
        );
        if (templateSetting && templateSetting.value !== '0')
          setDefaultRejectionTemplateId(+templateSetting.value);
      }
    };
    getSettings();
  }, []);

  useEffect(() => {
    const getRatingReasonSettings = async () => {
      const yellow: RatingReason[] = [];
      const blue: RatingReason[] = [];
      const red: RatingReason[] = [];
      const ratingReasons = await GetCachedRatingReasons(false);
      if (ratingReasons) {
        ratingReasons.sort(compareRatingReasons);
        for (let i = 0; i < ratingReasons.length; i++) {
          const rr = ratingReasons[i];
          if (rr.actionType === 2) yellow.push(rr);
          else if (rr.actionType === 1) blue.push(rr);
          else if (rr.actionType === 0) red.push(rr);
        }
        setYellowReasons(yellow);
        setBlueReasons(blue);
        setRedReasons(red);
      }
    };
    getRatingReasonSettings();
  }, []);

  useEffect(() => {
    const getActivitySettings = async () => {
      const res = await GetActivitiesSettings(3, undefined, errorHandler);
      if (res) setActivitySettings(res);
    };
    getActivitySettings();
  }, [errorHandler]);

  const selectedCandidateIds = useMemo(() => {
    if (selectedCardData) return [selectedCardData.id];
    return [];
  }, [selectedCardData]);

  const handleContextMenu = useCallback((event: MouseEvent) => {
    const t = event.target as HTMLElement;
    if (t.tagName.toLowerCase() === 'a') {
      setContextMenuAnchor(null);
      return;
    }

    event.preventDefault();
    const ct = event.currentTarget as HTMLElement;
    const rowId = +(ct.dataset['rowid'] ?? 0);

    const api = kanbanRef.current;
    if (api?.getCard && rowId) {
      const card = api.getCard(rowId);
      if (card?.data) setSelectedCardData(card.data);
    }

    const getBoundingClientRect = () => {
      return new DOMRect(event.clientX + 2, event.clientY - 6, 1, 1);
    };

    setContextMenuAnchor({ getBoundingClientRect });
  }, []);

  useEffect(() => {
    const getData = async () => {
      const res = await GetMarketingPlatformsAvailable();
      if (res) {
        setHasMailChimpAccess(res.mailChimp);
        setHasCampaignMonitorAccess(res.campaignMonitor);
      }
    };
    userCanEmailMarket && getData();
  }, [userCanEmailMarket]);

  useEffect(() => {
    if (isSetupFinished) {
      addContextMenuListeners(rowIds, handleContextMenu);

      return () => {
        removeContextMenuListeners(rowIds, handleContextMenu);
      };
    }
  }, [isSetupFinished, rowIds, styleUpdaterTrigger, handleContextMenu]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const rating = urlParams.get('RatingID');
    if (rating) {
      const parsed = +rating;
      if (parsed === 0 || parsed === 1 || parsed === 2 || parsed === 3 || parsed === 4)
        setRatingIdFilter(parsed);
    }
    setIsInitialQuerySetupFinished(true);
  }, []);

  useEffect(() => {
    const getCounts = async () => {
      const res = await GetJobCandidateRatingCounts(jobId, advertId);
      if (res) setRatingCounts([res.red, res.blue, res.yellow, res.unrated, res.green, res.total]);
    };
    getCounts();
  }, [advertId, jobId]);

  const generatQueryString = useCallback(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (ratingIdFilter || ratingIdFilter === 0)
      urlParams.set('RatingID', ratingIdFilter.toString());
    else urlParams.delete('RatingID');
    return urlParams.toString();
  }, [ratingIdFilter]);

  useEffect(() => {
    if (isInitialQuerySetupFinished) {
      const url = window.location.pathname + '?' + generatQueryString();
      navigate(url);
    }
  }, [generatQueryString, isInitialQuerySetupFinished, navigate]);

  useEffect(() => {
    if (isInitialQuerySetupFinished && rawData) {
      const kanbanContainer = document.getElementById(kanbanElementDomId);
      if (kanbanContainer && rawData) {
        const cardColumns: KanbanGroupingColumn[] = [];
        kanbanGroupings.map((k) => {
          if (rawData) {
            const count = rawData.filter((r) => r.kanbanGroupingID == k.id);
            cardColumns.push({ id: k.id, label: `${k.name} [${count.length}]` });
          }
        });
        const cardData: KanbanCard[] = [];
        for (let i = 0; i < rawData.length; i++) {
          const d = rawData[i];

          if (ratingIdFilter === undefined || ratingIdFilter === d.ratingID) {
            const cardColor = (ratingStyle as any)[d.ratingID] ?? {
              background: 'white',
              text: 'black',
            };

            cardData.push({
              id: d.jobTrackingID,
              column: d.kanbanGroupingID,
              title: d.fullName,
              candidateID: d.id,
              jobID: d.jobID,
              ratingBgColor: cardColor.background,
              ratingTextColor: cardColor.text,
              statusName: d.currentStatusName,
              statusLastUpdatedValue: d.statusLastUpdatedValue,
              source: d.source,
              data: d,
            });
          }
        }

        let kanbanResult: KanbanApi | null = null;

        if (cardColumns.length > 0) {
          kanbanResult = new (window as any).kanban.Kanban(`#${kanbanElementDomId}`, {
            readonly: true,
            scrollType: 'column',
            columns: cardColumns,
            cards: cardData,
            cardTemplate: (params: any) => {
              const { cardFields } = params;
              const {
                id,
                candidateID,
                jobID,
                title,
                source,
                statusLastUpdatedValue,
                statusName,
                ratingBgColor,
                ratingTextColor,
              } = cardFields;
              return `
    <div id="${getKanbanCardDomId(id)}" data-rowid="${id}" style='padding: 10px 5px; border-left: 0.5rem solid ${ratingBgColor}; border-radius: var(--wx-kanban-card-border-radius)'>
        <div><a style="color: #008299; font-weight: bold;" href='/candidates/${candidateID}?jobId=${jobID}'>${title}</a></div>
        ${source ? `<div>${source}</div>` : ''}
        <div>${statusLastUpdatedValue}</div>
        <div style='display: flex; justify-content: end'>
            ${ratingBgColor !== 'white' ? `<div style="background: ${ratingBgColor}; color: ${ratingTextColor}; ${KanbanCardTagStyle}">${statusName}</div>` : ''}
        </div>
    </div>
    `;
            },
          });
          kanbanRef.current = kanbanResult;
        }
        setStyleUpdaterTrigger((prev) => !prev);
        setIsSetupFinished(true);

        return () => {
          if (kanbanResult) kanbanResult.destructor();
        };
      }
    }
  }, [jobId, advertId, rawData, isInitialQuerySetupFinished, ratingIdFilter, kanbanGroupings]);

  const ratingFilterActions = useMemo(() => {
    const size = 30;
    return (
      <Box display="flex" gap="15px" alignItems="center">
        <Badge badgeContent={ratingCounts[4]} color="primary" max={10000}>
          <Avatar
            onClick={() => setRatingIdFilter((prev) => (prev === 4 ? undefined : 4))}
            sx={{
              color: ratingIdFilter === 4 ? 'black' : undefined,
              bgcolor: greenBgColor,
              width: size,
              height: size,
              cursor: 'pointer',
            }}
          >
            <PersonIcon />
          </Avatar>
        </Badge>
        <Badge badgeContent={ratingCounts[2]} color="primary" max={10000}>
          <Avatar
            onClick={() => setRatingIdFilter((prev) => (prev === 2 ? undefined : 2))}
            sx={{
              color: ratingIdFilter === 2 ? 'black' : undefined,
              bgcolor: yellowBgColor,
              width: size,
              height: size,
              cursor: 'pointer',
            }}
          >
            <PersonIcon />
          </Avatar>
        </Badge>
        <Badge badgeContent={ratingCounts[1]} color="primary" max={10000}>
          <Avatar
            onClick={() => setRatingIdFilter((prev) => (prev === 1 ? undefined : 1))}
            sx={{
              color: ratingIdFilter === 1 ? 'black' : undefined,
              bgcolor: blueBgColor,
              width: size,
              height: size,
              cursor: 'pointer',
            }}
          >
            <PersonIcon />
          </Avatar>
        </Badge>
        <Badge badgeContent={ratingCounts[0]} color="primary" max={10000}>
          <Avatar
            onClick={() => setRatingIdFilter((prev) => (prev === 0 ? undefined : 0))}
            sx={{
              color: ratingIdFilter === 0 ? 'black' : undefined,
              bgcolor: redBgColor,
              width: size,
              height: size,
              cursor: 'pointer',
            }}
          >
            <PersonIcon />
          </Avatar>
        </Badge>
        <Badge badgeContent={ratingCounts[3]} color="primary" max={10000}>
          <Avatar
            onClick={() => setRatingIdFilter((prev) => (prev === 3 ? undefined : 3))}
            sx={{
              color: ratingIdFilter === 3 ? 'black' : undefined,
              width: size,
              height: size,
              cursor: 'pointer',
            }}
          >
            <PersonIcon />
          </Avatar>
        </Badge>
        <Badge badgeContent={ratingCounts[5]} color="primary" max={10000}>
          <Avatar
            onClick={() => setRatingIdFilter(undefined)}
            sx={{ width: size, height: size, cursor: 'pointer' }}
          >
            <FilterAltOffIcon />
          </Avatar>
        </Badge>
      </Box>
    );
  }, [ratingCounts, ratingIdFilter]);

  const sendMessageCallback = useCallback(
    (
      type: 'email' | 'sms' | 'marketing-list',
      idKey: keyof JobTracking,
      nameKey: keyof JobTracking,
      emailOrPhoneKey: keyof JobTracking
    ) => {
      if (selectedCardData) {
        const row = selectedCardData;
        const recipients: EmailRecipient[] = [];

        const recipientId = row[idKey] as number;
        const recipientName = row[nameKey] as string;
        const emailOrPhone = row[emailOrPhoneKey] as string;
        const email = type === 'email' || type === 'marketing-list' ? emailOrPhone : '';
        const phone = type === 'sms' ? emailOrPhone : '';

        recipients.push({
          id: recipientId,
          email: email,
          name: recipientName,
          mobile: phone,
          isOptOut: row.optOut,
          jobTracking: row,
        });
        setSendMessageDialogType(type);
        setMessageRecipients(recipients);
      }
    },
    [selectedCardData]
  );
  const exportToMailchimpOrCampaignMonitorCallback = useCallback(
    (dialogType: 'mailchimp' | 'campaign-monitor') => {
      if (selectedCardData) {
        const row = selectedCardData;
        const exportRecordsData: RecordExportData[] = [];
        exportRecordsData.push({
          id: row.id,
          email: row.emailAddress,
          firstName: row.firstName,
          lastName: row.lastName,
          isOptOut: row.optOut,
          candidate: row,
        });
        setExportRecordDialogType(dialogType);
        setExportRecords(exportRecordsData);
      }
    },
    [selectedCardData]
  );

  const handleUnlinkFromJob = useCallback(() => {
    if (selectedCardData) {
      const unlink: UnlinkCandidateData = {
        candidateId: selectedCardData.id,
        applicationId: selectedCardData.applicationID,
        candidateName: selectedCardData.fullName,
        furthestStatus: selectedCardData.furthestStatusName,
        source: selectedCardData.source,
      };
      setUnlinkCandidateData([unlink]);
    }
  }, [selectedCardData]);

  const actionsMenuDefinition = useMemo<MenuOptionDefinition[]>(() => {
    const hasMarketingPlatform = hasMailChimpAccess || hasCampaignMonitorAccess;
    const contactIdsQuery = encodeURIComponent(selectedCandidateIds.join(','));

    const actions: MenuOptionDefinition[] = [
      {
        label: 'Export Marketing List',
        type: 'action',
        allow: () => userCanEmailMarket && !hasMarketingPlatform,
        action: () => sendMessageCallback('marketing-list', 'id', 'fullName', 'emailAddress'),
      },
      {
        label: 'Export to Mailchimp',
        type: 'action',
        allow: () => userCanEmailMarket && hasMailChimpAccess,
        action: () => exportToMailchimpOrCampaignMonitorCallback('mailchimp'),
      },
      {
        label: 'Export to Campaign Monitor',
        type: 'action',
        allow: () => userCanEmailMarket && hasCampaignMonitorAccess,
        action: () => exportToMailchimpOrCampaignMonitorCallback('campaign-monitor'),
      },
      {
        label: 'Send Email',
        type: 'action',
        allow: () => userCanSendEmail,
        action: () => sendMessageCallback('email', 'id', 'fullName', 'emailAddress'),
      },
      {
        label: 'Send SMS',
        type: 'action',
        allow: () => userCanSendSms,
        action: () => sendMessageCallback('sms', 'id', 'fullName', 'mobile_Standardized'),
      },
      {
        label: 'Tags',
        type: 'action',
        allow: () => userCanAddEditCandidates,
        action: () => setShowTagManagementDialog(true),
      },
      {
        label: 'Link To Job',
        type: 'action',
        action: () => setShowLinkCandidateToJobDialog(true),
      },
      {
        label: 'Unlink From Job',
        type: 'action',
        action: handleUnlinkFromJob,
      },
    ];

    for (let i = 0; i < activitySettings.length; i++) {
      const setting = activitySettings[i];
      if (setting.category === 2) continue;
      if (setting.workflowStatus === 0) {
        if (setting.id === 23)
          actions.push({
            label: setting.customName,
            type: 'action',
            action: () => setShowSendReferralDialog(true),
          });
        else
          actions.push({
            label: setting.customName,
            type: 'action',
            action: () => setSelectedActivity(setting),
          });
      }
    }

    const meetingActions: MenuOptionDefinition = {
      label: 'Meetings',
      type: 'parent',
      subMenu: [],
    };

    if (meetingTypes.length > 0 && meetingActions.subMenu) {
      for (let i = 0; i < meetingTypes.length; i++) {
        const t = meetingTypes[i];
        const href = `/meetings/create?typeId=${t.id}&contactIds=${contactIdsQuery}&source=${entityTypeId}`;
        meetingActions.subMenu.push({ label: t.name, type: 'link', href: href });
      }
    }

    if (meetingActions.subMenu && meetingActions.subMenu.length > 0) actions.push(meetingActions);

    return actions;
  }, [
    hasMailChimpAccess,
    hasCampaignMonitorAccess,
    handleUnlinkFromJob,
    meetingTypes,
    canExportCandidates,
    userCanEmailMarket,
    sendMessageCallback,
    exportToMailchimpOrCampaignMonitorCallback,
    userCanAddEditCandidates,
    userCanSendEmail,
    userCanSendSms,
    activitySettings,
    selectedCandidateIds,
  ]);

  const verifyRejectionEmails = useCallback(async () => {
    if (selectedCardData?.applicationID) {
      const verificationData = await VerifyRejectionEmailByCandidateList(
        [selectedCardData.id],
        jobId,
        errorHandler
      );
      if (verificationData) {
        const needRejectionEmail = verificationData.filter((d) => !d.rejectionSentDate);
        if (needRejectionEmail.length > 0) return needRejectionEmail.map((r) => r.candidateID);
      }
    }
    return [];
  }, [errorHandler, jobId, selectedCardData]);

  const bulkRateCallback = useCallback(
    async (actionId: number, reason: string, isContinue = false) => {
      setContextMenuAnchor(null);
      loadingHandler?.(true);

      if (actionId === 0 && !isContinue) {
        const needRejection = await verifyRejectionEmails();
        if (needRejection.length > 0) {
          setRejectionEmailRecipients(needRejection);
          setBulkRejectionReason(reason);
          loadingHandler?.(false);
          return;
        }
      }

      const res = await UpdateCandidateJobRating_Bulk(
        selectedCandidateIds,
        jobId,
        actionId,
        reason,
        errorHandler
      );
      if (res) {
        successHandler?.('Record rating changed');
        getKanbanData();
        return;
      }
      loadingHandler?.(false);
    },
    [
      errorHandler,
      jobId,
      loadingHandler,
      selectedCandidateIds,
      successHandler,
      verifyRejectionEmails,
      getKanbanData,
    ]
  );

  const bulkRateActions = useMemo<MenuOptionDefinition | null>(() => {
    const yellowOptions: MenuOptionDefinition[] = yellowReasons.map<MenuOptionDefinition>((r) => ({
      label: r.value,
      type: 'action',
      action: () => bulkRateCallback(2, r.value),
    })); // 2
    const blueOptions: MenuOptionDefinition[] = blueReasons.map<MenuOptionDefinition>((r) => ({
      label: r.value,
      type: 'action',
      action: () => bulkRateCallback(1, r.value),
    })); // 1
    const redOptions: MenuOptionDefinition[] = redReasons.map<MenuOptionDefinition>((r) => ({
      label: r.value,
      type: 'action',
      action: () => bulkRateCallback(0, r.value),
    })); // 0

    return {
      label: 'Rate Candidate',
      type: 'parent',
      orderIndex: -1,
      subMenu: [
        { label: 'Grey', type: 'action', action: () => bulkRateCallback(3, '') },
        {
          label: 'Yellow',
          type: yellowReasons.length > 0 ? 'parent' : 'action',
          subMenu: yellowOptions,
          action: () => bulkRateCallback(2, ''),
        },
        {
          label: 'Blue',
          type: blueReasons.length > 0 ? 'parent' : 'action',
          subMenu: blueOptions,
          action: () => bulkRateCallback(1, ''),
        },
        {
          label: 'Red',
          type: redReasons.length > 0 ? 'parent' : 'action',
          subMenu: redOptions,
          action: () => bulkRateCallback(0, ''),
        },
      ],
    };
  }, [blueReasons, bulkRateCallback, redReasons, yellowReasons]);

  const workflowMenuDefinition = useMemo<MenuOptionDefinition[]>(() => {
    const candidateIdsQuery = encodeURIComponent(selectedCandidateIds.join(','));
    const candidateId = selectedCandidateIds[0] ?? 0;
    const workflow: MenuOptionDefinition[] = [
      {
        label: 'Export to Deputy',
        type: 'action',
        action: () => setShowExportToDeputyDialog(true),
        allow: () => isDeputyConnected,
      },
      {
        label: 'Export to foundU',
        type: 'action',
        action: () => setShowExportToFoundUDialog(true),
        allow: () => isFoundUConnected,
      },
    ];

    const offerOptions: MenuOptionDefinition = {
      label: 'Offers',
      type: 'parent',
      subMenu: [],
      orderIndex: workflowOrder.offerIndex,
    };
    const interviewActions: MenuOptionDefinition = {
      label: 'Interviews',
      type: 'parent',
      subMenu: [],
      orderIndex: workflowOrder.interviewIndex,
    };
    const referenceCheckActions: MenuOptionDefinition = {
      label: 'Ref Checks',
      type: 'parent',
      subMenu: [],
      orderIndex: workflowOrder.refCheckIndex,
    };

    if (jobId) {
      if (selectedCardData && selectedCardData.ratingID === 4)
        workflow.push({
          label: 'Submit Candidate',
          type: 'link',
          href: `/submissions/create?candidateIds=${candidateIdsQuery}&jobId=${jobId}`,
          orderIndex: workflowOrder.submitIndex,
        });
      else
        workflow.push({
          label: 'Submit Candidate',
          type: 'action',
          action: () => errorHandler?.('Only green candidates can be submitted'),
          orderIndex: workflowOrder.submitIndex,
        });
      for (let i = 0; i < activitySettings.length; i++) {
        const s = activitySettings[i];
        if (s.workflowStatus === 0) continue;
        if (interviewWorkflowStatuses.indexOf(s.workflowStatus) > -1) continue;
        if (s.id === 10)
          workflow.push({
            label: s.customName,
            type: 'link',
            href: `/candidates/${candidateId}/screening?jobId=${jobId}`,
            orderIndex: workflowOrder.indexes[s.workflowStatus],
          });
        else if (s.id === 16) {
          workflow.push({
            label: 'Place Candidate',
            type: 'action',
            action: () => setShowPlaceCandidatesDialog(true),
            orderIndex: workflowOrder.indexes[s.workflowStatus],
          });
        } else if (s.id === 30) {
          referenceCheckActions.subMenu?.push({
            label: s.customName,
            type: 'link',
            href: `/candidates/${candidateId}/reference-check?jobId=${jobId}`,
          });
          referenceCheckActions.subMenu?.push({
            label: 'Digital Reference Check',
            type: 'action',
            action: () => setShowDigitalReferenceCheckDialog(true),
          });
          referenceCheckActions.subMenu?.push({
            label: 'Referoo',
            type: 'action',
            allow: () => isReferooConnected,
            action: () => setShowReferooDialog(true),
          });
        } else if (
          s.workflowStatus === offerWorkflowStatus ||
          s.id === 39 ||
          s.workflowStatus === offerAcceptedWorkflowStatus
        ) {
          if (s.id === 14)
            offerOptions.subMenu?.push({
              label: s.customName,
              type: 'action',
              action: () => setShowOfferMadeDialog(true),
            });
          else
            offerOptions.subMenu?.push({
              label: s.customName,
              type: 'action',
              action: () => setSelectedActivity(s),
            });
        } else if (s.id === 9 || s.id === 40 || s.id === 50 || s.id === 73) {
          workflow.push({
            label: s.customName,
            type: 'action',
            action: () => setSelectedActivity(s),
            orderIndex: workflowOrder.indexes[s.workflowStatus],
          });
        } else if (s.workflowStatus > 0 && s.id !== 15) {
          workflow.push({
            label: s.customName,
            type: 'action',
            action: () => setSelectedActivity(s),
            orderIndex: workflowOrder.indexes[s.workflowStatus],
          });
        }
      }

      if (interviewTypes.length > 0 && interviewActions.subMenu) {
        for (let i = 0; i < interviewTypes.length; i++) {
          const t = interviewTypes[i];
          const href = `/interviews/create?typeId=${t.id}&candidateId=${candidateId}&jobId=${jobId}`;
          interviewActions.subMenu.push({
            label: t.name,
            type: 'link',
            href: href,
          });
        }
      }
    }

    if (offerOptions.subMenu && offerOptions.subMenu.length > 0) workflow.push(offerOptions);
    if (referenceCheckActions.subMenu && referenceCheckActions.subMenu.length > 0)
      workflow.push(referenceCheckActions);
    if (interviewActions.subMenu && interviewActions.subMenu.length > 0) {
      interviewActions.subMenu.sort(sortDefinitionByLabel);
      workflow.push(interviewActions);
    }
    if (bulkRateActions) workflow.push(bulkRateActions);

    return workflow;
  }, [
    selectedCardData,
    selectedCandidateIds,
    workflowOrder.offerIndex,
    workflowOrder.interviewIndex,
    workflowOrder.refCheckIndex,
    workflowOrder.submitIndex,
    workflowOrder.indexes,
    jobId,
    bulkRateActions,
    isDeputyConnected,
    isFoundUConnected,
    interviewTypes,
    errorHandler,
    activitySettings,
    interviewWorkflowStatuses,
    offerWorkflowStatus,
    offerAcceptedWorkflowStatus,
    isReferooConnected,
  ]);

  const contextMenuDefinition = useMemo<MenuOptionDefinition[]>(() => {
    actionsMenuDefinition.sort(sortDefinitionByLabel);
    workflowMenuDefinition.sort(sortDefinitionByLabel);
    return [
      { label: 'Actions', type: 'parent', subMenu: actionsMenuDefinition },
      { label: 'Workflow', type: 'parent', subMenu: workflowMenuDefinition },
    ];
  }, [actionsMenuDefinition, workflowMenuDefinition]);

  const selectedActivityData = useMemo<Activity | null>(() => {
    if (selectedCardData && selectedActivity && selectedActivity.workflowStatus !== 0) {
      return {
        ...DefaultActivity,
        candidateID: selectedCardData.id,
        candidateName: selectedCardData.fullName,
        jobID: selectedCardData.jobID,
        jobReference: job ? job.jobReference : '',
        jobTitle: job ? job.title : '',
        clientID: job ? job.clientID : 0,
        clientName: job ? job.clientName : '',
        contactID: job ? job.contact1ID : 0,
        contactName: job ? job.contact1Name : '',
        category: selectedActivity.category,
        type: selectedActivity.customName,
        typeID: selectedActivity.id,
      };
    }
    if (selectedCardData && job && selectedActivity) {
      return {
        ...DefaultActivity,
        candidateID: selectedCardData.id,
        candidateName: selectedCardData.fullName,
        jobID: job ? job.jobID : 0,
        jobReference: job ? job.jobReference : '',
        jobTitle: job ? job.title : '',
        category: selectedActivity.category,
        type: selectedActivity.customName,
        typeID: selectedActivity.id,
      };
    }
    if (selectedCardData && selectedActivity) {
      return {
        ...DefaultActivity,
        candidateID: selectedCardData.id,
        candidateName: selectedCardData.fullName,
        category: selectedActivity.category,
        type: selectedActivity.customName,
        typeID: selectedActivity.id,
      };
    }
    return null;
  }, [selectedCardData, job, selectedActivity]);

  const rejectionEmailSentSuccess = useCallback(
    (isContinue = false) => {
      setRejectionEmailRecipients([]);
      !isContinue && successHandler?.('Rejection Email sent');
      bulkRateCallback(0, bulkRejectionReason, isContinue);
    },
    [bulkRateCallback, bulkRejectionReason, successHandler]
  );

  const exportToDeputyCallback = useCallback(async () => {
    setContextMenuAnchor(null);
    if (selectedCardData) {
      loadingHandler?.(true);
      const res = await ExportCandidateToDeputy(selectedCardData.id, errorHandler);
      if (res) {
        setShowExportToDeputyDialog(false);
        successHandler?.('Record Exported to Deputy');
      }
      loadingHandler?.(false);
    }
  }, [selectedCardData, loadingHandler, errorHandler, successHandler]);

  const exportToFoundUCallback = useCallback(async () => {
    setContextMenuAnchor(null);
    if (selectedCardData) {
      loadingHandler?.(true);
      const res = await ExportCandidateToFoundU(selectedCardData.id, errorHandler);
      if (res) {
        setShowExportToFoundUDialog(false);
        successHandler?.('Record Exported to FoundU');
      }
      loadingHandler?.(false);
    }
  }, [selectedCardData, loadingHandler, errorHandler, successHandler]);

  const successHandlerWithDataReload = useCallback(
    (message: string) => {
      successHandler?.(message);
      getKanbanData();
    },
    [successHandler, getKanbanData]
  );

  const isAnyDialogOpen = useMemo(() => {
    return (
      Boolean(sendMessageDialogType) ||
      showTagManagementDialog ||
      showLinkCandidateToJobDialog ||
      unlinkCandidateData.length > 0 ||
      Boolean(selectedActivity) ||
      showSendReferralDialog ||
      showExportToDeputyDialog ||
      showExportToFoundUDialog ||
      showPlaceCandidatesDialog ||
      showDigitalReferenceCheckDialog ||
      showReferooDialog ||
      showOfferMadeDialog ||
      rejectionEmailRecipients.length > 0
    );
  }, [
    sendMessageDialogType,
    showTagManagementDialog,
    showLinkCandidateToJobDialog,
    unlinkCandidateData.length,
    selectedActivity,
    showSendReferralDialog,
    showExportToDeputyDialog,
    showExportToFoundUDialog,
    showPlaceCandidatesDialog,
    showDigitalReferenceCheckDialog,
    showReferooDialog,
    showOfferMadeDialog,
    rejectionEmailRecipients.length,
  ]);

  useEffect(() => {
    if (isAnyDialogOpen) {
      setContextMenuAnchor(null);
    }
  }, [isAnyDialogOpen]);

  const closeContexMenuHandler = useCallback(() => {
    setContextMenuAnchor(null);
  }, []);

  return (
    <>
      <ExportRecordsDialog
        open={sendMessageDialogType === 'marketing-list'}
        closeHandler={() => setSendMessageDialogType(null)}
        entityType="candidates"
        filename="Candidates"
        records={messageRecipients}
        includeTimeStamp
      />
      <ExportRecordsToMailchimpDialog
        open={exportRecordDialogType === 'mailchimp'}
        closeHandler={() => setExportRecordDialogType(null)}
        records={exportRecords}
        loadingHandler={loadingHandler}
        successHandler={successHandler}
        errorHandler={errorHandler}
      />
      <ExportRecordsToCampaignMonitorDialog
        open={exportRecordDialogType === 'campaign-monitor'}
        closeHandler={() => setExportRecordDialogType(null)}
        records={exportRecords}
        loadingHandler={loadingHandler}
        successHandler={successHandler}
        errorHandler={errorHandler}
      />
      <SendEmailDialog
        open={sendMessageDialogType === 'email'}
        sourceEntityId={entityTypeId}
        recipients={messageRecipients}
        recipientEntityTypeId={entityTypeId}
        closeHandler={() => setSendMessageDialogType(null)}
        loadingHandler={loadingHandler}
        errorHandler={errorHandler}
        successHandler={successHandler}
        jobId={jobId}
      />
      <SendSmsDialog
        open={sendMessageDialogType === 'sms'}
        sourceEntityId={entityTypeId}
        recipients={messageRecipients}
        recipientEntityTypeId={entityTypeId}
        closeHandler={() => setSendMessageDialogType(null)}
        loadingHandler={loadingHandler}
        errorHandler={errorHandler}
        successHandler={successHandler}
        jobId={jobId}
      />
      <LinkCandidatesToJobDialog
        open={showLinkCandidateToJobDialog && selectedCandidateIds.length > 0}
        closeHandler={() => setShowLinkCandidateToJobDialog(false)}
        candidateIds={selectedCandidateIds}
        loadingHandler={loadingHandler}
        errorHandler={errorHandler}
        successHandler={successHandlerWithDataReload}
      />
      <UnlinkCandidatesFromJobDialog
        open={unlinkCandidateData.length > 0}
        closeHandler={() => setUnlinkCandidateData([])}
        data={unlinkCandidateData}
        jobId={jobId}
        loadingHandler={loadingHandler}
        errorHandler={errorHandler}
        successHandler={successHandlerWithDataReload}
      />
      <TagsManagementDialog
        open={showTagManagementDialog && selectedCandidateIds.length > 0}
        closeHandler={() => setShowTagManagementDialog(false)}
        entityId={entityTypeId}
        recordIds={selectedCandidateIds}
        loadingHandler={loadingHandler}
        errorHandler={errorHandler}
        successHandler={successHandler}
      />
      <EditActivityDialog
        closeHandler={() => setSelectedActivity(null)}
        data={selectedActivity ? selectedActivityData : null}
        open={Boolean(selectedActivity) && Boolean(selectedCardData)}
        loadingHandler={loadingHandler}
        errorHandler={errorHandler}
        successHandler={successHandlerWithDataReload}
        entityTypeId={entityTypeId}
      />
      <SendReferralDialog
        open={showSendReferralDialog && Boolean(selectedCardData)}
        candidateId={selectedCardData ? selectedCardData.id : 0}
        candidateName={selectedCardData ? selectedCardData.fullName : ''}
        closeHandler={() => setShowSendReferralDialog(false)}
        loadingHandler={loadingHandler}
        errorHandler={errorHandler}
        successHandler={successHandler}
      />
      <ConfirmationDialog
        message="Are you sure you want to export the selected record to Deputy?"
        onClose={() => setShowExportToDeputyDialog(false)}
        onContinue={exportToDeputyCallback}
        open={showExportToDeputyDialog}
        title="Confirm Action"
        confirmActionText="Yes"
        cancelActionText="No"
      />
      <ConfirmationDialog
        message="Are you sure you want to export the selected record to foundU?"
        onClose={() => setShowExportToFoundUDialog(false)}
        onContinue={exportToFoundUCallback}
        open={showExportToFoundUDialog}
        title="Confirm Action"
        confirmActionText="Yes"
        cancelActionText="No"
      />
      <PlaceCandidatesDialog
        open={showPlaceCandidatesDialog}
        candidateIds={selectedCandidateIds}
        jobId={jobId}
        jobType={job ? job.type : ''}
        closeHandler={() => setShowPlaceCandidatesDialog(false)}
        canBulkPlace={canBulkPlacements}
        loadingHandler={loadingHandler}
        errorHandler={errorHandler}
      />
      <DigitalReferenceCheckDialog
        jobId={jobId}
        open={showDigitalReferenceCheckDialog}
        candidateId={selectedCandidateIds.length === 1 ? +selectedCandidateIds[0] : 0}
        closeHandler={() => setShowDigitalReferenceCheckDialog(false)}
        loadingHandler={loadingHandler}
        errorHandler={errorHandler}
        successHandler={successHandlerWithDataReload}
      />
      <ReferooActionsDialog
        open={showReferooDialog}
        jobId={jobId}
        candidateId={selectedCandidateIds.length === 1 ? +selectedCandidateIds[0] : 0}
        closeHandler={() => setShowReferooDialog(false)}
        loadingHandler={loadingHandler}
        errorHandler={errorHandler}
        successHandler={successHandlerWithDataReload}
      />
      <OfferMadeDialog
        open={showOfferMadeDialog}
        candidateId={selectedCardData ? selectedCardData.id : 0}
        jobId={jobId}
        closeHandler={() => setShowOfferMadeDialog(false)}
        loadingHandler={loadingHandler}
        errorHandler={errorHandler}
        successHandler={successHandlerWithDataReload}
      />
      <RejectionEmailDialog
        open={rejectionEmailRecipients.length > 0}
        candidateIds={rejectionEmailRecipients}
        jobId={jobId}
        defaultRejectionTemplateId={defaultRejectionTemplateId}
        closeHandler={() => setRejectionEmailRecipients([])}
        onRejectionEmailSent={rejectionEmailSentSuccess}
        errorHandler={errorHandler}
      />
      <Box>
        {extraActions}
        {
          <Box pb="10px" sx={{ float: 'right' }}>
            {ratingFilterActions}
          </Box>
        }
      </Box>
      <ContextMenu
        anchorEl={contextMenuAnchor}
        closeHandler={closeContexMenuHandler}
        definition={contextMenuDefinition}
        placement="bottom-start"
      />
      <div style={{ display: 'flex', flex: '1', flexDirection: 'column', position: 'relative' }}>
        <div
          style={{
            display: 'flex',
            flex: '1',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            id={kanbanElementDomId}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '100%',
              overflow: 'auto',
            }}
          />
        </div>
      </div>
    </>
  );
}
